import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { USERNAME_STYLES, BANNER_BADGES, AVATAR_FRAMES, CARD_BACKGROUNDS, WEAPONS } from "../constants/loadout";
import { useAuth } from "../context/AuthContext";
import { Crown, Mail, X, Shield, Trophy, Activity, CheckCircle2, Server, Gamepad2, Quote } from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";

const cache = new Map();
const CACHE_TTL = 10000;
const isTouch = typeof window !== "undefined" && ("ontouchstart" in window || navigator.maxTouchPoints > 0);

const PROFILE_BANNERS = {
  tactical: "https://images.unsplash.com/photo-1542751371-adc38448a05e",
  neon_grid: "https://images.unsplash.com/photo-1538481199705-c710c4e965fc",
  carbon: "https://images.unsplash.com/photo-1551103782-8ab07afd45c1",
  blood: "https://images.unsplash.com/photo-1614680376573-df3480f0c6ff",
  ice: "https://images.unsplash.com/photo-1547036967-23d11aacaee0",
  default: ((process.env.PUBLIC_URL || "/forum") + "/banners/default_profile_banner.png"),
};

export default function MemberHoverCard({ user: seedUser, children }) {
  const { user: viewer } = useAuth() || {};
  const [show, setShow] = useState(false);
  const [user, setUser] = useState(seedUser || null);
  const [memberNum, setMemberNum] = useState(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const wrapRef = useRef(null);
  const hoverTimer = useRef(null);
  const closeTimer = useRef(null);

  const username = seedUser?.username;

  useEffect(() => {
    if (!show || !username) return;
    const cached = cache.get(username);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      setUser(cached.user);
      setMemberNum(cached.memberNum);
      return;
    }
    api.get(`/users/${username}`).then((r) => {
      const data = { user: r.data.user, memberNum: r.data.member_number || null, timestamp: Date.now() };
      cache.set(username, data);
      setUser(data.user);
      setMemberNum(data.memberNum);
    }).catch(() => setUser(seedUser));
  }, [show, username, seedUser]);

  const positionCard = () => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    const cardW = 340, cardH = 460;
    const vw = window.innerWidth, vh = window.innerHeight;
    let left = rect.left;
    let top = rect.bottom + 4;
    if (top + cardH > vh - 20) top = Math.max(12, rect.top - cardH - 4);
    if (left + cardW > vw - 12) left = vw - cardW - 12;
    if (left < 12) left = 12;
    setPos({ top, left });
  };

  const open = () => { positionCard(); setShow(true); };
  const close = () => setShow(false);

  const handleEnter = () => {
    if (isTouch) return;
    clearTimeout(closeTimer.current);
    clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(open, 180);
  };
  const handleLeave = () => {
    if (isTouch) return;
    clearTimeout(hoverTimer.current);
    closeTimer.current = setTimeout(close, 400);
  };
  const handleClick = (e) => {
    if (isTouch || e.altKey || e.metaKey) {
      e.preventDefault(); e.stopPropagation();
      show ? close() : open();
    }
  };

  useEffect(() => {
    return () => {
      clearTimeout(hoverTimer.current);
      clearTimeout(closeTimer.current);
    };
  }, []);

  useEffect(() => {
    if (!show) return;
    const h = (e) => {
      if (!wrapRef.current?.contains(e.target)) {
        const card = document.getElementById("tf-hover-card");
        if (card && !card.contains(e.target)) close();
      }
    };
    document.addEventListener("touchstart", h);
    document.addEventListener("mousedown", h);
    window.addEventListener("scroll", close, { passive: true });
    return () => {
      document.removeEventListener("touchstart", h);
      document.removeEventListener("mousedown", h);
      window.removeEventListener("scroll", close);
    };
  }, [show]);

  if (!seedUser) return children;
  const safeUser = user || seedUser;
  const loadout = safeUser?.loadout || {};
  
  // Resolve Card Background from VIP Loadout
  const cardBgOpt = CARD_BACKGROUNDS.find((b) => b.id === loadout.card_background);
  const customBgStyle = loadout.custom_card_banner 
    ? { backgroundImage: `url(${loadout.custom_card_banner})`, backgroundSize: "cover", backgroundPosition: "center" }
    : ((cardBgOpt && cardBgOpt.id !== "none" && cardBgOpt.style) 
      ? cardBgOpt.style 
      : (safeUser.banner_url ? { backgroundImage: `url(${safeUser.banner_url})`, backgroundSize: "cover", backgroundPosition: "center" } : { background: "#101720" }));
  
  // Resolve Weapon from VIP Loadout
  const weaponObj = WEAPONS.find((w) => w.name === loadout.favorite_weapon || w.id === loadout.favorite_weapon);

  const isAdminViewer = viewer && (viewer.role === "admin" || viewer.role === "root");
  const isAdminTarget = safeUser?.role === "admin" || safeUser?.role === "root";

  return (
    <>
      <span ref={wrapRef} onMouseEnter={handleEnter} onMouseLeave={handleLeave} onClick={handleClick} className="inline-block idg-hover-trigger" style={{ verticalAlign: "baseline" }}>
        {children}
      </span>
      {show && safeUser && createPortal(
        <div
          id="tf-hover-card"
          onMouseEnter={() => clearTimeout(closeTimer.current)}
          onMouseLeave={handleLeave}
          className={`fixed w-[340px] rounded-xl border border-white/20 overflow-hidden tf-fade-in idg-hover-card idg-member-tooltip--${safeUser.id} idg-member-tooltip--${safeUser.username} ${cardBgOpt?.className || ""}`}
          style={{
            position: "fixed",
            top: pos.top,
            left: pos.left,
            zIndex: 99999,
            boxShadow: `0 25px 60px rgba(0,0,0,0.92), 0 0 35px ${(safeUser.is_vip || (safeUser.vip_tier && safeUser.vip_tier !== "none")) ? 'rgba(255, 159, 28, 0.35)' : 'rgba(0,0,0,0.6)'}`,
            ...customBgStyle
          }}
          data-testid={`hover-card-${safeUser.username}`}
        >
          {/* Bridge: invisible 12px above the card so cursor can transit without close */}
          <div className="absolute -top-3 left-0 right-0 h-3 pointer-events-auto" />
          
          {/* Background Video Loop (if user imported an animated video banner) */}
          {(loadout.custom_card_banner?.endsWith(".mp4") || (loadout.card_background === "custom" && loadout.custom_bg_video?.endsWith(".mp4"))) && (
            <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
              <video
                key={loadout.custom_card_banner || loadout.custom_bg_video}
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-full object-cover opacity-80 filter saturate-125"
                src={loadout.custom_card_banner || loadout.custom_bg_video}
              />
            </div>
          )}

          {/* Transparent Glass Tint Layer (Allows custom VIP Card Background to shine through vividly) */}
          <div className="bg-gradient-to-b from-black/25 via-black/45 to-black/75 text-white backdrop-blur-[1px] relative z-10">
            
            {/* Top Bar with VIP badge indicator and Close */}
            <div className="flex items-center justify-between px-3.5 pt-3 pb-1">
              {(safeUser.is_vip || (safeUser.vip_tier && safeUser.vip_tier !== "none")) ? (
                <div className="flex items-center gap-1 px-2 py-0.5 rounded bg-gradient-to-r from-amber-500 to-orange-500 text-black font-extrabold text-[10px] tracking-wider uppercase shadow-sm">
                  <Crown className="w-3 h-3 text-black fill-black" /> VIP MEMBER
                </div>
              ) : (
                <span className="text-[10px] uppercase font-bold tracking-widest text-zinc-400">COMMUNITY MEMBER</span>
              )}
              <button onClick={close} className="w-6 h-6 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded transition-colors" data-testid="hover-close">
                <X className="w-3.5 h-3.5 text-zinc-300" />
              </button>
            </div>

            {/* Header: Avatar, Frame, Animated Username, Badges */}
            <div className="px-3.5 py-2 flex items-start gap-3">
              {/* Avatar + Frame */}
              {(() => {
                const frameId = safeUser?.loadout?.avatar_frame || "none";
                const frame = AVATAR_FRAMES.find((f) => f.id === frameId) || AVATAR_FRAMES[0];
                const initial = (safeUser.username?.[0] || "?").toUpperCase();
                const size = 56;
                const innerSize = size - 6;
                return (
                  <div
                    className={`tf-frame ${frame.className || ""} inline-flex items-center justify-center tf-avatar-pop relative flex-shrink-0`}
                    style={{ width: size, height: size, padding: frame.id === "none" || frame.imageUrl ? 0 : 3 }}
                  >
                    {frame.imageUrl && (
                      <img src={frame.imageUrl} alt="Frame" className="absolute top-0 left-0 w-full h-full pointer-events-none z-10 scale-110" />
                    )}
                    <div
                      className="bg-zinc-900 text-zinc-200 font-bold flex items-center justify-center w-full h-full rounded overflow-hidden"
                      style={{ fontFamily: "Rajdhani", fontSize: innerSize * 0.4 }}
                    >
                      {safeUser.avatar_url ? (
                        <img src={safeUser.avatar_url} alt={safeUser.username} className="w-full h-full object-cover" />
                      ) : (
                        initial
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Username + VIP Banner Badges */}
              <div className="flex-1 min-w-0">
                <Link to={`/u/${safeUser.username}`} onClick={close} className="block truncate">
                  {(() => {
                    const styleId = safeUser?.loadout?.username_style || "default";
                    const style = USERNAME_STYLES.find((s) => s.id === styleId) || USERNAME_STYLES[0];
                    const nameText = safeUser.username || "";
                    const chars = nameText.split("");
                    const total = chars.length;
                    const center = (total - 1) / 2;

                    if (styleId === "default") {
                      return (
                        <span className="text-base font-bold drop-shadow text-white tracking-wide" data-text={safeUser.username}>
                          {safeUser.username}
                        </span>
                      );
                    }

                    return (
                      <span 
                        className={`text-base font-bold drop-shadow splitting words chars ${style.className}`}
                        style={{
                          "--word-total": 1,
                          "--char-total": total,
                          "--word-center": 0,
                          "--char-center": center,
                        }}
                        data-text={safeUser.username}
                      >
                        <span className="word" style={{ "--word-index": 0, "--word-total": 1 }}>
                          {chars.map((ch, idx) => (
                            <span
                              key={idx}
                              className="char"
                              data-char={ch}
                              style={{
                                "--char-index": idx,
                                "--char-total": total,
                                "--char-center": center,
                                "--char-percent": total > 1 ? idx / (total - 1) : 0,
                                "--char-offset": idx - center,
                              }}
                            >
                              {ch}
                            </span>
                          ))}
                        </span>
                      </span>
                    );
                  })()}
                </Link>

                {/* Banner Badges row */}
                <div className="flex items-center gap-1.5 flex-wrap mt-1">
                  {(() => {
                    const badgeId = safeUser.loadout?.banner_badge || safeUser.vip_tier;
                    if (!badgeId || badgeId === "none") return null;
                    const badge = BANNER_BADGES.find((b) => b.id === badgeId || b.className?.includes(badgeId)) || BANNER_BADGES.find((b) => b.id === "goldPulseAnimated") || BANNER_BADGES[1];
                    if (!badge || badge.id === "none") return null;
                    return (
                      <span className={`userBanner ${badge.className || "userBanner--orange"}`} style={badge.color ? { backgroundColor: badge.color } : {}}>
                        <span className="userBanner-before"></span>
                        <strong>{badge.text || badge.label || "FORUMS VIP"}</strong>
                        <span className="userBanner-after"></span>
                      </span>
                    );
                  })()}
                  
                  {safeUser.role === "root" && (
                    <span className="userBanner userBanner--yellow notranslate" translate="no">
                      <span className="userBanner-before"></span>
                      <strong>ROOT</strong>
                      <span className="userBanner-after"></span>
                    </span>
                  )}
                  {safeUser.role === "admin" && safeUser.role !== "root" && (
                    <span className="userBanner userBanner--red notranslate" translate="no">
                      <span className="userBanner-before"></span>
                      <strong>ADMIN</strong>
                      <span className="userBanner-after"></span>
                    </span>
                  )}
                  {safeUser.role === "mod" && (
                    <span className="userBanner userBanner--royalBlue notranslate" translate="no">
                      <span className="userBanner-before"></span>
                      <strong>MODERATOR</strong>
                      <span className="userBanner-after"></span>
                    </span>
                  )}
                  {safeUser.group_name && safeUser.role !== "root" && safeUser.role !== "admin" && (
                    <span 
                      className="px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-widest rounded bg-white/10 text-zinc-300 border border-white/15 notranslate"
                      style={{ fontFamily: 'Rajdhani', fontWeight: 800 }}
                      translate="no"
                    >
                      {safeUser.group_name}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Personal Quote if configured */}
            {loadout.personal_quote && (
              <div className="mx-3.5 my-1.5 p-2 bg-amber-500/10 border-l-2 border-amber-500 rounded text-[11px] text-amber-200/90 italic flex items-center gap-1.5">
                <Quote className="w-3 h-3 text-amber-400 shrink-0" />
                <span className="truncate">"{loadout.personal_quote}"</span>
              </div>
            )}

            {/* Member Info: Joined & Last Seen */}
            <div className="px-3.5 py-1 text-[11px] text-zinc-400 flex items-center justify-between border-t border-white/5 mt-1 pt-1.5">
              <span>Member <strong className="text-zinc-200">#{memberNum || 1}</strong></span>
              <span>Joined: <strong className="text-zinc-300">{safeUser.joined_at ? format(new Date(safeUser.joined_at), "PP") : "Aug 10, 2026"}</strong></span>
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-3 border-y border-white/5 py-2 mx-3.5 my-1.5 text-center bg-black/30 rounded">
              <div>
                <div className="text-[9px] font-extrabold tracking-wider uppercase text-orange-400">MESSAGES</div>
                <div className="text-base font-bold text-white tf-mono">{safeUser.message_count || 0}</div>
              </div>
              <div>
                <div className="text-[9px] font-extrabold tracking-wider uppercase text-orange-400">REACTION SCORE</div>
                <div className="text-base font-bold text-orange-500 tf-mono">{safeUser.reaction_score || 0}</div>
              </div>
              <div>
                <div className="text-[9px] font-extrabold tracking-wider uppercase text-orange-400">POINTS</div>
                <div className="text-base font-bold text-white tf-mono">{safeUser.points || 0}</div>
              </div>
            </div>

            {/* Game Statistics & Steam & Favorite Weapon Card */}
            <div className="mx-3.5 p-2.5 rounded bg-black/40 border border-white/5 space-y-2 text-[11px]">
              {/* Steam Row */}
              <div className="flex items-center justify-between text-zinc-300">
                <div className="flex items-center gap-1.5">
                  <svg className="w-3.5 h-3.5 fill-current text-[#66c0f4]" viewBox="0 0 24 24">
                    <path d="M12 2a10 10 0 0 0-10 10c0 4.7 3.25 8.64 7.64 9.68l3.1-4.5a3.5 3.5 0 0 1 1.76.07l2.5 3.65A10 10 0 0 0 22 12a10 10 0 0 0-10-10m-3.5 13.5a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3m6-4a2 2 0 1 1 0-4 2 2 0 0 1 0 4"/>
                  </svg>
                  <span className="font-semibold text-white">{safeUser.steam_name || safeUser.username}</span>
                </div>
                <span className="text-[10px] text-emerald-400 font-bold flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3 text-emerald-400" /> VAC Clean
                </span>
              </div>

              {/* Last Server Played Row */}
              <div className="flex items-center justify-between text-zinc-300">
                <div className="flex items-center gap-1.5 truncate">
                  <Server className="w-3.5 h-3.5 text-orange-400 shrink-0" />
                  <span className="truncate text-zinc-200">DOPE.INDUNGI.PRO | Romanian Legends</span>
                </div>
                <span className="text-[10px] text-emerald-400 font-bold shrink-0 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span> 18 ms
                </span>
              </div>

              {/* Favorite Weapon Row */}
              {(weaponObj || loadout.favorite_weapon) && (
                <div className="flex items-center justify-between border-t border-white/5 pt-1.5">
                  <span className="text-[10px] text-zinc-400 uppercase font-bold">FAVORITE WEAPON</span>
                  <div className="flex items-center gap-1.5 text-amber-300 font-bold">
                    {weaponObj?.iconUrl && (
                      <img src={weaponObj.iconUrl} alt={weaponObj.name} className="h-4 object-contain filter drop-shadow" />
                    )}
                    <span>{weaponObj?.name || loadout.favorite_weapon}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Trophies Row */}
            {safeUser.trophies?.length > 0 && (
              <div className="px-3.5 py-1.5 flex items-center gap-1.5 text-sm">
                <Trophy className="w-3.5 h-3.5 text-yellow-500 shrink-0" />
                <div className="flex gap-1 flex-wrap">
                  {safeUser.trophies.slice(0, 7).map((tr) => (
                    <span key={tr} title={tr.replace(/_/g, " ")}>{TROPHY_ICONS[tr] || "🏅"}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Bottom Actions */}
            <div className="p-3.5 flex items-center gap-2 border-t border-white/5 bg-black/40">
              <Link
                to={`/u/${safeUser.username}`}
                onClick={close}
                className="flex-1 py-1.5 px-3 bg-white/10 hover:bg-white/20 text-white rounded text-center text-xs font-bold uppercase tracking-wider transition-colors"
                data-testid={`hover-view-${safeUser.username}`}
              >
                VIEW PROFILE
              </Link>
              {viewer && viewer.username !== safeUser.username && (
                <Link
                  to={`/messages?dm=${safeUser.username}`}
                  onClick={close}
                  className="py-1.5 px-3 bg-blue-600 hover:bg-blue-500 text-white rounded text-xs font-bold uppercase tracking-wider flex items-center gap-1 transition-colors"
                  data-testid={`hover-dm-${safeUser.username}`}
                >
                  <Mail className="w-3 h-3" /> MESSAGE
                </Link>
              )}
              {isAdminViewer && isAdminTarget && (
                <Link
                  to="/admin"
                  onClick={close}
                  className="py-1.5 px-3 bg-gradient-to-r from-amber-500 to-orange-500 text-black font-extrabold text-xs uppercase tracking-wider rounded flex items-center gap-1 shadow-md hover:brightness-110 transition-all"
                  data-testid={`hover-admincp-${safeUser.username}`}
                >
                  <Shield className="w-3 h-3 fill-black" /> ADMINCP
                </Link>
              )}
            </div>

          </div>
        </div>,
        document.body
      )}
    </>
  );
}

const TROPHY_ICONS = {
  first_post: "🌱", ten_posts: "💬", hundred_posts: "📣", five_hundred_posts: "🔥",
  first_topic: "📌", ten_topics: "📚",
  first_reaction: "❤️", fifty_reactions: "⭐", hundred_reactions: "💯",
  monthly_winner: "🏆", vip_member: "👑", veteran: "🛡️", elite: "💎", founder: "🌟", donator: "💝",
};
